<script setup>
import { computed, watch, defineProps, defineEmits } from 'vue';
import {
  MAP_ANIMATION_PRESETS,
  MAP_ANIMATION_OPERATORS,
  isAnimationNone,
  mapAnimationPreviewClass,
  mapAnimationPreviewStyle,
  availableOperatorsForMetric,
} from '@/composables/useQuickMetrics';

const props = defineProps({
  metric: { type: Object, required: true },
  sourceNames: { type: Array, default: () => [] },
  fieldOptions: { type: Object, default: () => ({}) },
  /** Raw source options map {source: {field: type}} used to detect string fields */
  fieldTypes: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:metric', 'remove']);

function update(changes) {
  emit('update:metric', { ...props.metric, ...changes });
}

function updateRule(changes) {
  update({ mapAnimationRule: { ...props.metric.mapAnimationRule, ...changes } });
}

function onSourceChange(source) {
  update({ source, field: '' });
}

const STRING_ONLY_OPERATORS = MAP_ANIMATION_OPERATORS.filter((op) => op.value === '=' || op.value === '!=');

const NUMERIC_TYPES = new Set(['number', 'int', 'integer', 'float', 'double', 'long', 'decimal', 'numeric', 'uint']);

const isNonNumericField = computed(() => {
  const source = props.metric?.source;
  const field = props.metric?.field;
  if (!source || !field) return false;
  const type = props.fieldTypes?.[source]?.[field];
  if (typeof type !== 'string' || !type) return false;
  return !NUMERIC_TYPES.has(type.toLowerCase());
});

const effectiveOperators = computed(() =>
  isNonNumericField.value ? STRING_ONLY_OPERATORS : availableOperatorsForMetric(props.metric)
);

watch(isNonNumericField, (nonNumeric) => {
  if (nonNumeric && !['=', '!='].includes(props.metric.mapAnimationRule?.operator)) {
    updateRule({ operator: '=' });
  }
});

function onThresholdInput(event) {
  const value = event.target.value;
  const updatedRule = { ...props.metric.mapAnimationRule, value };
  const validOps = isNonNumericField.value
    ? ['=', '!=']
    : availableOperatorsForMetric({ mapAnimationRule: updatedRule }).map((op) => op.value);
  if (!validOps.includes(updatedRule.operator)) {
    updatedRule.operator = '=';
  }
  update({ mapAnimationRule: updatedRule });
}
</script>

<template>
  <div class="rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-white/5 overflow-hidden">
    <!-- Top row: source / field / name / unit / remove -->
    <div class="grid grid-cols-1 md:grid-cols-12 gap-2 p-3">
      <div class="md:col-span-3">
        <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Source</label>
        <select
          :value="metric.source"
          @change="onSourceChange($event.target.value)"
          class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark"
        >
          <option value="">Select…</option>
          <option v-for="source in sourceNames" :key="source" :value="source">{{ source }}</option>
        </select>
      </div>
      <div class="md:col-span-3">
        <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Field</label>
        <select
          :value="metric.field"
          :disabled="!metric.source"
          @change="update({ field: $event.target.value })"
          class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark disabled:opacity-60"
        >
          <option value="">Select…</option>
          <option v-for="field in (fieldOptions[metric.source] || [])" :key="field" :value="field">{{ field }}</option>
        </select>
      </div>
      <div class="md:col-span-3">
        <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Display Name</label>
        <input
          :value="metric.friendlyName"
          type="text"
          placeholder="Optional"
          @input="update({ friendlyName: $event.target.value })"
          class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark"
        />
      </div>
      <div class="md:col-span-2">
        <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Unit</label>
        <input
          :value="metric.unit"
          type="text"
          placeholder="Optional"
          @input="update({ unit: $event.target.value })"
          class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark"
        />
      </div>
      <div class="md:col-span-1 flex items-end">
        <button
          type="button"
          @click="emit('remove')"
          class="w-full px-2 py-1.5 text-xs font-medium text-mc-red border border-mc-red/30 rounded-mc-sm hover:bg-mc-red/5 dark:border-mc-red/40 dark:hover:bg-mc-red/10 transition-colors duration-160"
        >
          ×
        </button>
      </div>
    </div>

    <!-- Map animation sub-row -->
    <div class="px-3 pb-3 pt-2 border-t border-mc-steel/40 dark:border-line-on-dark bg-mc-cloud/60 dark:bg-black/20">
      <div class="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Map Animation Rule</div>
      <div class="flex flex-wrap gap-2 items-end">
        <div class="flex-shrink-0 w-12">
          <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Preview</label>
          <div class="h-[30px] flex items-center">
            <div class="map-animation-preview" :class="mapAnimationPreviewClass(metric)" :style="mapAnimationPreviewStyle(metric)">
              <span class="map-animation-preview__pin">
                <span class="map-animation-preview__core" />
              </span>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 w-28">
          <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Animation</label>
          <select
            :value="metric.mapAnimationRule.animation"
            @change="updateRule({ animation: $event.target.value })"
            class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark"
          >
            <option v-for="preset in MAP_ANIMATION_PRESETS" :key="preset" :value="preset">{{ preset }}</option>
          </select>
        </div>
        <div class="flex-shrink-0 w-14" v-if="['glow', 'ping'].includes(metric.mapAnimationRule.animation)">
          <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Color</label>
          <input
            :value="metric.mapAnimationRule.color"
            type="color"
            @input="updateRule({ color: $event.target.value })"
            class="w-full h-[30px] p-0.5 border border-mc-steel dark:border-line-on-dark rounded-mc-sm bg-mc-white dark:bg-mc-charcoal cursor-pointer"
          />
        </div>
        <div class="flex-shrink-0 w-36">
          <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Operator</label>
          <select
            :value="metric.mapAnimationRule.operator"
            :disabled="isAnimationNone(metric)"
            @change="updateRule({ operator: $event.target.value })"
            class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark disabled:opacity-60"
          >
            <option v-for="op in effectiveOperators" :key="op.value" :value="op.value">{{ op.label }}</option>
          </select>
        </div>
        <template v-if="metric.mapAnimationRule.operator === 'between'">
          <div class="flex-1 min-w-[80px]">
            <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Min</label>
            <input
              :value="metric.mapAnimationRule.min"
              type="text"
              :disabled="isAnimationNone(metric)"
              @input="updateRule({ min: $event.target.value })"
              class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark disabled:opacity-60"
            />
          </div>
          <div class="flex-1 min-w-[80px]">
            <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Max</label>
            <input
              :value="metric.mapAnimationRule.max"
              type="text"
              :disabled="isAnimationNone(metric)"
              @input="updateRule({ max: $event.target.value })"
              class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark disabled:opacity-60"
            />
          </div>
        </template>
        <div v-else class="flex-1 min-w-[80px]">
          <label class="block font-mono text-[11px] uppercase tracking-wide text-ink-muted dark:text-on-dark-muted mb-1">Threshold</label>
          <input
            :value="metric.mapAnimationRule.value"
            type="text"
            :disabled="isAnimationNone(metric)"
            @input="onThresholdInput($event)"
            class="input-base text-xs py-1.5 dark:bg-mc-charcoal dark:text-on-dark disabled:opacity-60"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.map-animation-preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 18px;
  height: 20px;
  color: #3b82f6;
  overflow: visible;
}

.map-animation-preview::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  background: transparent;
  border: 0 solid transparent;
  transform: translate(-50%, -50%) scale(0.7);
  opacity: 0;
  z-index: 0;
  pointer-events: none;
  filter: blur(3px);
}

.map-animation-preview__pin {
  position: relative;
  width: 14px;
  height: 14px;
  background: #3b82f6;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.35);
  z-index: 1;
}

.map-animation-preview__pin::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  top: 2px;
  left: 2px;
  border-radius: 9999px;
  border: 1.5px solid rgba(255, 255, 255, 0.95);
  box-sizing: border-box;
}

.map-animation-preview__core {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #ffffff;
  transform: translate(-50%, -50%);
}

.map-animation-preview--pulse { animation: map-preview-pulse 1.4s ease-in-out infinite; }
.map-animation-preview--bounce { animation: map-preview-bounce 1s ease-in-out infinite; }
.map-animation-preview--glow::after { animation: map-preview-glow-halo 1.6s ease-in-out infinite; }
.map-animation-preview--ping::after { animation: map-preview-ping-ring 1.2s ease-out infinite; }
.map-animation-preview--shake { animation: map-preview-shake 0.7s linear infinite; }

@keyframes map-preview-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.12); }
}

@keyframes map-preview-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

@keyframes map-preview-glow-halo {
  0%, 100% {
    transform: translate(-50%, -50%) scale(0.7);
    opacity: 0.14;
    background: var(--map-preview-glow-color, #3b82f6);
    border-width: 0;
    border-color: transparent;
    filter: blur(3px);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.45);
    opacity: 0.45;
    background: var(--map-preview-glow-color, #3b82f6);
    border-width: 0;
    border-color: transparent;
    filter: blur(6px);
  }
}

@keyframes map-preview-ping-ring {
  0% {
    transform: translate(-50%, -50%) scale(0.45);
    opacity: 0.85;
    border-width: 2px;
    border-color: var(--map-preview-glow-color, #3b82f6);
    filter: blur(0);
  }
  100% {
    transform: translate(-50%, -50%) scale(1.65);
    opacity: 0;
    border-width: 1px;
    border-color: var(--map-preview-glow-color, #3b82f6);
    filter: blur(0);
  }
}

@keyframes map-preview-shake {
  0% { transform: translateX(0); }
  20% { transform: translateX(-2px); }
  40% { transform: translateX(2px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
  100% { transform: translateX(0); }
}
</style>
